import { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/system";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  selectBizMobileDashboardLoading,
  selectBizMobileOutputMetadata,
  selectBizMobileQueryOutput,
  selectBizMobileComparisonQueryOutput,
  selectBizMobileComparisonLoading,
} from "../../../../../../../features/bizDevelopmentDashboardMobileSlice";
import { useBizDevelopmentDashboardMobile } from "../../../../../../../pages/BizDevelopment/Dashboard/hooks/useBizDevelopmentDashboardMobile";
import {
  buildColumns,
  exportToCSV,
  sortRows,
} from "../../../../helpers/bizDevelopmentDashboardHelpers";
import { spacing } from "../../../../../../../assets/styles/tokens";
import PerformanceDateFilter from "./PerformanceMobileContent/PerformanceDateFilter";
import PerformanceItem from "./PerformanceMobileContent/PerformanceItem";
import MobileFloatingActionButton from "../../../../../../../pages/FloatingItems/MobileFloatingActionButton";

export interface TimeframeOption {
  value: string;
  label: string;
  shortLabel?: string;
  sublabel?: string;
}

const fmt = (d: dayjs.Dayjs) => d.format("DD/MM/YY");

const Performance = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const queryOutput = useSelector(selectBizMobileQueryOutput);
  const outputMetadata = useSelector(selectBizMobileOutputMetadata);
  const isLoading = useSelector(selectBizMobileDashboardLoading);
  const comparisonQueryOutput = useSelector(
    selectBizMobileComparisonQueryOutput,
  );
  const isComparisonLoading = useSelector(selectBizMobileComparisonLoading);

  const {
    fetchDashboardData,
    fetchComparisonData,
    clearComparisonData,
    getSiteFromWorkspace,
  } = useBizDevelopmentDashboardMobile();

  const timeframeOptions = useMemo(
    (): TimeframeOption[] => [
      {
        value: "yesterday",
        label: `Yesterday (${fmt(dayjs().subtract(1, "day"))})`,
        shortLabel: "Yesterday",
        sublabel: `Comparing ${fmt(dayjs().subtract(1, "day"))}`,
      },
      {
        value: "two_days_ago",
        label: `2 Days Ago (${fmt(dayjs().subtract(2, "day"))})`,
        shortLabel: "2 Days Ago",
        sublabel: `Comparing ${fmt(dayjs().subtract(2, "day"))}`,
      },
      {
        value: "three_days_ago",
        label: `3 Days Ago (${fmt(dayjs().subtract(3, "day"))})`,
        shortLabel: "3 Days Ago",
        sublabel: `Comparing ${fmt(dayjs().subtract(3, "day"))}`,
      },
      { value: "mtd", label: "MTD", shortLabel: "MTD" },
    ],
    [],
  );

  const [selectedTimeframe, setSelectedTimeframe] = useState<TimeframeOption>(
    timeframeOptions.find(
      (o) =>
        o.value === (searchParams.get("timeframePerformance") ?? "yesterday"),
    ) ?? timeframeOptions[0],
  );

  const [selectedAdditional, setSelectedAdditional] =
    useState<TimeframeOption | null>(() => {
      const saved = searchParams.get("additionalTimeframePerformance");
      return saved
        ? (timeframeOptions.find((o) => o.value === saved) ?? null)
        : null;
    });

  const additionalTimeframeOptions = useMemo(
    (): TimeframeOption[] =>
      timeframeOptions.filter(
        (o) => o.value !== "mtd" && o.value !== selectedTimeframe.value,
      ),
    [selectedTimeframe, timeframeOptions],
  );

  const workspaceParam = searchParams.get("workspace");
  const prevWorkspaceRef = useRef<string | null>(null);

  // Re-fetch whenever workspace changes (covers initial mount + workspace switch)
  useEffect(() => {
    const sites = getSiteFromWorkspace();
    if (sites.length === 0) return;

    const isWorkspaceChange =
      prevWorkspaceRef.current !== null &&
      prevWorkspaceRef.current !== workspaceParam;
    prevWorkspaceRef.current = workspaceParam;

    if (isWorkspaceChange) {
      // Workspace switched — clear comparison state and re-fetch
      setSelectedAdditional(null);
      clearComparisonData();
      setSearchParams((prev) => {
        prev.delete("additionalTimeframePerformance");
        return prev;
      });
    } else {
      // Initial mount — restore comparison if it was already in URL
      const additionalTf = searchParams.get("additionalTimeframePerformance");
      if (additionalTf) {
        fetchComparisonData(additionalTf, sites);
      }
    }

    fetchDashboardData(selectedTimeframe.value, sites);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceParam]);

  const sortKey =
    searchParams.get("sortKeyPerformance") ?? "brand_display_name";
  const sortDir = (searchParams.get("sortDirPerformance") ?? "asc") as
    | "asc"
    | "desc";

  const isMtd = selectedTimeframe.value === "mtd";
  const isComparisonActive = selectedAdditional !== null;

  const sortedRows = useMemo(
    () => sortRows(queryOutput, sortKey, sortDir),
    [queryOutput, sortKey, sortDir],
  );

  const handleTimeframeChange = (newValue: TimeframeOption) => {
    setSelectedTimeframe(newValue);
    setSelectedAdditional(null);
    clearComparisonData();
    setSearchParams((prev) => {
      prev.set("timeframePerformance", newValue.value);
      prev.delete("additionalTimeframePerformance");
      return prev;
    });
    fetchDashboardData(newValue.value, getSiteFromWorkspace());
  };

  const handleAdditionalChange = (newValue: TimeframeOption | null) => {
    setSelectedAdditional(newValue);
    setSearchParams((prev) => {
      if (newValue) {
        prev.set("additionalTimeframePerformance", newValue.value);
      } else {
        prev.delete("additionalTimeframePerformance");
      }
      return prev;
    });
    if (newValue) {
      fetchComparisonData(newValue.value, getSiteFromWorkspace());
    } else {
      clearComparisonData();
    }
  };

  const handleRefresh = () => {
    const sites = getSiteFromWorkspace();
    fetchDashboardData(selectedTimeframe.value, sites);
    if (selectedAdditional) {
      fetchComparisonData(selectedAdditional.value, sites);
    }
  };

  const handleDownload = () => {
    const columns = buildColumns(outputMetadata, isMtd);
    exportToCSV({
      columns,
      primaryRows: queryOutput,
      comparisonRows: isComparisonActive ? (comparisonQueryOutput ?? []) : [],
      primaryLabel: selectedTimeframe.label,
      comparisonLabel: selectedAdditional?.label ?? "",
      isMtd,
      filename: `performance_${selectedTimeframe.value}${selectedAdditional ? `_vs_${selectedAdditional.value}` : ""}`,
    });
  };

  const handleSortApply = (key: string, dir: "asc" | "desc") => {
    setSearchParams((prev) => {
      prev.set("sortKeyPerformance", key);
      prev.set("sortDirPerformance", dir);
      return prev;
    });
  };

  return (
    <Box sx={{ px: 2 }}>
      <PerformanceDateFilter
        timeframeOptions={timeframeOptions}
        selectedTimeframe={selectedTimeframe}
        onTimeframeChange={handleTimeframeChange}
        additionalOptions={additionalTimeframeOptions}
        selectedAdditional={selectedAdditional}
        onAdditionalChange={handleAdditionalChange}
        onRefresh={handleRefresh}
        onDownload={handleDownload}
        brandCount={sortedRows.length}
        isMtd={isMtd}
        isComparisonActive={isComparisonActive}
        sortKey={sortKey}
        sortDir={sortDir}
        onSortApply={handleSortApply}
        outputMetadata={outputMetadata ?? {}}
      />
      <Box sx={{ mt: "-" + spacing.Spacing.md.$value }}>
        <PerformanceItem
          queryOutput={sortedRows}
          comparisonQueryOutput={comparisonQueryOutput ?? []}
          isLoading={isLoading}
          isComparisonLoading={isComparisonLoading}
          isMtd={isMtd}
          isComparisonActive={isComparisonActive}
          outputMetadata={outputMetadata ?? {}}
        />
      </Box>
      <MobileFloatingActionButton trackerDataId="812" saveTrackerDataId="813" />
    </Box>
  );
};

export default Performance;
